import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Spinner, Alert, Image, Form, Button } from 'react-bootstrap';
import { api } from '../services/api';
import CheckerImageTooltip from './CheckerImageTooltip';
import { IconAssortmentMatrix } from './Icons';

function similarityColor(score: number): string {
  const t = Math.max(0, Math.min(1, (score - 0.4) / 0.6));
  return `hsl(${Math.round(t * 120)} 75% 58%)`;
}

export interface CheckerItem {
  sku: string;
  article: string;
  subject_name: string;
  subject_name_eng: string;
  subject_name_cn: string;
  thumb_url: string;
  url: string;
  score?: number | null;
  in_registry?: boolean;
}

export interface ProductItem {
  id: string;
  article: string | null;
  name: string;
  eng_name: string;
  subject_id: number;
  subject_name_ru: string;
  subject_name_en: string;
  thumb_url: string;
  url: string;
  supplier_web_link: string;
  final_solution: string;
  score?: number | null;
  in_registry?: boolean;
}

interface CheckerContentProps {
  initialSku?: string;
  initialSearchAsSubject?: boolean;
  onInitialSearch?: (sku: string) => void;
}

export const CheckerContent: React.FC<CheckerContentProps> = ({ initialSku = '', initialSearchAsSubject = false, onInitialSearch }) => {
  const [skuInput, setSkuInput] = useState(initialSku);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checker, setChecker] = useState<CheckerItem[]>([]);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [visual, setVisual] = useState(false);

  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [hasCheckerMore, setHasCheckerMore] = useState(false);
  const [hasProductsMore, setHasProductsMore] = useState(false);
  const [loadingMoreChecker, setLoadingMoreChecker] = useState(false);
  const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
  const [searchedItem, setSearchedItem] = useState<{ thumb_url: string; label: string } | null>(null);
  const [imageTooltip, setImageTooltip] = useState<{
    show: boolean;
    searchedImage: { url: string; label: string } | null;
    foundImage: { url: string; label: string } | null;
    position: { x: number; y: number };
  }>({ show: false, searchedImage: null, foundImage: null, position: { x: 0, y: 0 } });
  const searchParamsRef = useRef<{ sku?: string; subject_name?: string }>({});
  const checkerLengthRef = useRef(0);
  const productsLengthRef = useRef(0);
  checkerLengthRef.current = checker.length;
  productsLengthRef.current = products.length;
  const searchInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const resultsScrollRef = useRef<HTMLDivElement>(null);
  const PAGE_SIZE = 50;

  const doSearch = useCallback(async (value: string, forceSubject?: boolean, appendChecker = false, appendProducts = false) => {
    if (!value.trim() && !appendChecker && !appendProducts) return;

    const params: { sku?: string; subject_name?: string; checker_limit?: number; checker_offset?: number; products_limit?: number; products_offset?: number } = {};
    if (!appendChecker && !appendProducts) {
      if (forceSubject || (value.trim().length > 1 && !/^\d+$/.test(value.trim()))) {
        params.subject_name = value.trim();
      } else {
        params.sku = value.trim();
      }
      params.checker_limit = PAGE_SIZE;
      params.checker_offset = 0;
      params.products_limit = PAGE_SIZE;
      params.products_offset = 0;
      setLoading(true);
      setError(null);
      setChecker([]);
      setProducts([]);
      setSearchedItem(null);
      setVisual(false);
      setShowSuggestions(false);
    } else {
      Object.assign(params, searchParamsRef.current);
      if (appendChecker) {
        params.checker_limit = PAGE_SIZE;
        params.checker_offset = checkerLengthRef.current;
        params.products_limit = 0;
        params.products_offset = 0;
      } else {
        params.checker_limit = 0;
        params.checker_offset = 0;
        params.products_limit = PAGE_SIZE;
        params.products_offset = productsLengthRef.current;
      }
      if (appendChecker) setLoadingMoreChecker(true);
      else setLoadingMoreProducts(true);
    }

    try {
      const response = await api.getCheckerSearch(params);
      const data = response.data;
      if (data.success) {
        if (appendChecker) {
          const newChecker = data.checker || [];
          setChecker(prev => [...prev, ...newChecker]);
          setHasCheckerMore(newChecker.length > 0 ? (data.hasCheckerMore ?? false) : false);
        } else if (appendProducts) {
          const newProducts = data.products || [];
          setProducts(prev => [...prev, ...newProducts]);
          setHasProductsMore(newProducts.length > 0 ? (data.hasProductsMore ?? false) : false);
        } else {
          setChecker(data.checker || []);
          setProducts(data.products || []);
          setSearchedItem(data.searchedItem || null);
          setVisual(!!data.visual);
          setHasCheckerMore(data.hasCheckerMore ?? false);
          setHasProductsMore(data.hasProductsMore ?? false);
          searchParamsRef.current = { sku: params.sku, subject_name: params.subject_name };
        }
      } else if (!appendChecker && !appendProducts) {
        setError(data.error || 'Ошибка поиска');
      }
    } catch (err: any) {
      if (!appendChecker && !appendProducts) {
        setError(err.response?.data?.error || err.message || 'Ошибка запроса');
      }
    } finally {
      setLoading(false);
      setLoadingMoreChecker(false);
      setLoadingMoreProducts(false);
    }
  }, []);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await api.getCheckerSuggestions(q.trim());
      if (res.data.success && Array.isArray(res.data.suggestions)) {
        setSuggestions(res.data.suggestions);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
      }
    } catch {
      setSuggestions([]);
    }
  }, []);

  const debouncedFetch = useMemo(() => {
    let t: ReturnType<typeof setTimeout>;
    return (q: string) => {
      clearTimeout(t);
      t = setTimeout(() => fetchSuggestions(q), 250);
    };
  }, [fetchSuggestions]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setSkuInput(v);
    debouncedFetch(v);
    if (!v || v.length < 2) setShowSuggestions(false);
  }, [debouncedFetch]);

  const handleSelectSuggestion = useCallback((subjectName: string) => {
    setSkuInput(subjectName);
    setShowSuggestions(false);
    setSuggestions([]);
    doSearch(subjectName, true);
  }, [doSearch]);

  const handleSearch = useCallback(() => {
    doSearch(skuInput);
  }, [skuInput, doSearch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (showSuggestions && suggestions.length > 0) {
        handleSelectSuggestion(suggestions[0]);
      } else {
        handleSearch();
      }
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node) &&
        searchInputRef.current && !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleImageMouseEnter = (
    foundImage: { url: string; label: string },
    event: React.MouseEvent,
    isSearchedBlock?: boolean
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const searchedImage = searchedItem ? { url: searchedItem.thumb_url, label: searchedItem.label } : null;
    setImageTooltip({
      show: true,
      searchedImage: isSearchedBlock ? foundImage : searchedImage,
      foundImage: isSearchedBlock ? null : foundImage,
      position: { x: rect.left + rect.width / 2, y: rect.top }
    });
  };

  const handleImageMouseLeave = () => {
    setImageTooltip({ show: false, searchedImage: null, foundImage: null, position: { x: 0, y: 0 } });
  };

  const handleOpenUrl = (url: string) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const loadMoreChecker = useCallback(() => {
    if (!hasCheckerMore || loadingMoreChecker || !searchParamsRef.current.sku && !searchParamsRef.current.subject_name) return;
    doSearch(skuInput, initialSearchAsSubject, true, false);
  }, [hasCheckerMore, loadingMoreChecker, skuInput, initialSearchAsSubject, doSearch]);

  const loadMoreProducts = useCallback(() => {
    if (!hasProductsMore || loadingMoreProducts || !searchParamsRef.current.sku && !searchParamsRef.current.subject_name) return;
    doSearch(skuInput, initialSearchAsSubject, false, true);
  }, [hasProductsMore, loadingMoreProducts, skuInput, initialSearchAsSubject, doSearch]);

  const hasCheckerMoreRef = useRef(false);
  const hasProductsMoreRef = useRef(false);
  hasCheckerMoreRef.current = hasCheckerMore;
  hasProductsMoreRef.current = hasProductsMore;

  const cards = useMemo(() => {
    const rows = [
      ...checker.map((row) => ({
        key: `c-${row.sku}`,
        href: row.url || '',
        thumb: row.thumb_url,
        label: row.article || row.sku,
        article: row.article || row.sku || '-',
        extra: '',
        in_registry: row.in_registry,
        score: row.score,
      })),
      ...products.map((row) => ({
        key: `p-${row.id}`,
        href: row.url || '',
        thumb: row.thumb_url,
        label: row.article || row.name || row.id,
        article: row.article || row.id || '-',
        extra: row.final_solution || 'pending',
        in_registry: row.in_registry,
        score: row.score,
      })),
    ];
    if (visual) rows.sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
    return rows;
  }, [checker, products, visual]);

  const handleResultsScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight <= el.clientHeight) return;
    if (el.scrollHeight - el.scrollTop > el.clientHeight + 80) return;
    if (hasCheckerMoreRef.current && !loadingMoreChecker) loadMoreChecker();
    else if (hasProductsMoreRef.current && !loadingMoreProducts) loadMoreProducts();
  }, [loadMoreChecker, loadMoreProducts, loadingMoreChecker, loadingMoreProducts]);

  useEffect(() => {
    if (initialSku) {
      setSkuInput(initialSku);
      doSearch(initialSku, initialSearchAsSubject);
      onInitialSearch?.(initialSku);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только при смене initialSku/initialSearchAsSubject, не при doSearch
  }, [initialSku, initialSearchAsSubject]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div className="mb-3 d-flex gap-2 align-items-center position-relative">
        <div ref={suggestionsRef} className="position-relative d-inline-block">
          <Form.Control
            ref={searchInputRef}
            type="text"
            placeholder="SKU или subject"
            value={skuInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            style={{ maxWidth: '280px' }}
          />
          {showSuggestions && suggestions.length > 0 && (
            <ul
              className="list-unstyled position-absolute mt-1 p-0 rounded border border-secondary bg-dark"
              style={{ maxHeight: 200, overflowY: 'auto', zIndex: 1050, minWidth: '100%' }}
            >
              {suggestions.map((s) => (
                <li
                  key={s}
                  role="button"
                  tabIndex={0}
                  className="px-3 py-2 small text-nowrap text-truncate"
                  style={{ maxWidth: 320, cursor: 'pointer' }}
                  onMouseDown={(e) => { e.preventDefault(); handleSelectSuggestion(s); }}
                >
                  {s}
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button variant="primary" onClick={handleSearch} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" /> : 'Найти'}
        </Button>
        {visual && <span className="text-muted small">Сравнение по фото, DINOv2</span>}
        <div
          className="d-flex align-items-center justify-content-center rounded border border-secondary bg-dark overflow-hidden flex-shrink-0"
          style={{ width: 64, height: 64 }}
          title={searchedItem?.label}
          onMouseEnter={(e) => searchedItem?.thumb_url && handleImageMouseEnter({ url: searchedItem.thumb_url, label: searchedItem.label }, e, true)}
          onMouseLeave={handleImageMouseLeave}
        >
          {searchedItem?.thumb_url ? (
            <Image
              src={searchedItem.thumb_url}
              alt={searchedItem.label}
              style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
            />
          ) : (
            <span className="text-muted small">—</span>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div
        ref={resultsScrollRef}
        className="flex-grow-1 overflow-auto border border-secondary rounded"
        style={{ minHeight: 0 }}
        onScroll={handleResultsScroll}
      >
        {cards.length > 0 ? (
          <div className="d-flex flex-wrap gap-2 p-2">
            {cards.map((row) => (
              <a
                key={row.key}
                href={row.href || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none text-light"
                style={{ flex: '1 1 140px', minWidth: 100, maxWidth: 180 }}
                onClick={(e) => { if (!row.href) e.preventDefault(); }}
              >
                <div
                  className="bg-dark border border-secondary rounded overflow-hidden position-relative"
                  style={{ aspectRatio: '1', cursor: 'pointer' }}
                  onMouseEnter={(e) => row.thumb && handleImageMouseEnter({ url: row.thumb, label: row.label }, e)}
                  onMouseLeave={handleImageMouseLeave}
                >
                  {row.in_registry && (
                    <span
                      title="Ассортиментная матрица"
                      className="position-absolute d-inline-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        top: 4,
                        right: 4,
                        width: 20,
                        height: 20,
                        background: 'rgba(25, 135, 84, 0.92)',
                        color: '#fff',
                        zIndex: 1,
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.25)'
                      }}
                    >
                      <IconAssortmentMatrix size={11} />
                    </span>
                  )}
                  {row.thumb ? (
                    <Image
                      src={row.thumb}
                      alt={row.label}
                      fluid
                      className="w-100 h-100"
                      style={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <div className="bg-secondary w-100 h-100 d-flex align-items-center justify-content-center">
                      <span className="text-muted small">—</span>
                    </div>
                  )}
                  {row.score != null && (
                    <span
                      className="position-absolute"
                      title="Похожесть"
                      style={{
                        right: 4,
                        bottom: 4,
                        zIndex: 1,
                        fontSize: 11,
                        lineHeight: 1,
                        fontWeight: 700,
                        padding: '3px 5px',
                        borderRadius: 4,
                        background: similarityColor(row.score),
                        color: '#1a1a1a',
                        boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
                      }}
                    >
                      {row.score.toFixed(2)}
                    </span>
                  )}
                </div>
                <div className="small mt-1 text-center text-truncate" title={row.extra ? `${row.article} | ${row.extra}` : row.article}>
                  {row.article}{row.extra ? ` | ${row.extra}` : ''}
                </div>
              </a>
            ))}
          </div>
        ) : !loading ? (
          <div className="text-muted small py-2 px-2">Нет данных. Введите SKU или subject и нажмите «Найти».</div>
        ) : null}
        {(loadingMoreChecker || loadingMoreProducts) && <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>}
      </div>

      <CheckerImageTooltip
        searchedImage={imageTooltip.searchedImage}
        foundImage={imageTooltip.foundImage}
        show={imageTooltip.show}
        position={imageTooltip.position}
      />
    </div>
  );
};
